import React, { useRef, useState } from "react";
import { Send, MapPin, Mail, Phone, CalendarIcon, Instagram, Facebook } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { useInView } from "../hooks/useInView";
import { eventTypes, contactInfo } from "../data/mock";
import { toast } from "sonner";
import { format } from "date-fns";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { threshold: 0.1 });
  const [date, setDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    eventType: "",
    venue: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.eventType || !date) {
      toast.error("Please fill in all required fields");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: formData.name,
        email: formData.email,
        event_date: date.toISOString(),
        event_type: formData.eventType,
        venue: formData.venue,
        message: formData.message,
      });
      toast.success(
        "Your inquiry has been sent! We'll be in touch within 24 hours."
      );
      setFormData({ name: "", email: "", eventType: "", venue: "", message: "" });
      setDate(null);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="section-padding relative"
      style={{ backgroundColor: "#0F0F0F" }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Info Side */}
          <div
            className={`${
              isInView ? "animate-slide-in-left" : "opacity-0"
            }`}
          >
            <span className="text-gold text-sm font-semibold tracking-widest uppercase mb-4 block">
              Get In Touch
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-6">
              Let's Create Something{" "}
              <span className="text-gold">Unforgettable</span>
            </h2>
            <p className="text-white/50 text-lg leading-relaxed mb-10">
              Ready to make your event extraordinary? Fill out the form and
              we'll get back to you within 24 hours to discuss your vision.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <div className="text-white/40 text-sm">Email</div>
                  <a href={`mailto:${contactInfo.email}`} className="text-white font-medium hover:text-gold">
                    {contactInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <div className="text-white/40 text-sm">Phone</div>
                  <a href="tel:+17204326407" className="text-white font-medium hover:text-gold">
                    {contactInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
                  <Instagram className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <div className="text-white/40 text-sm">Instagram</div>
                  <a
                    href={contactInfo.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white font-medium hover:text-gold"
                  >
                    {contactInfo.instagram}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
                  <Facebook className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <div className="text-white/40 text-sm">Facebook</div>
                  <a
                    href={contactInfo.facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white font-medium hover:text-gold"
                  >
                    {contactInfo.facebook}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <div className="text-white/40 text-sm">Location</div>
                  <div className="text-white font-medium">
                    {contactInfo.location}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form Side */}
          <div
            className={`${
              isInView ? "animate-slide-in-right" : "opacity-0"
            }`}
          >
            <form
              onSubmit={handleSubmit}
              className="border border-white/10 rounded-2xl p-8"
              style={{
                backgroundColor: "rgba(255,255,255,0.03)",
                backdropFilter: "blur(8px)",
              }}
            >
              <div className="space-y-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white/70 text-sm">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-xl focus:border-gold focus:ring-gold"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/70 text-sm">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-xl focus:border-gold focus:ring-gold"
                  />
                </div>

                {/* Event Date */}
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Event Date *</Label>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full justify-start text-left font-normal h-12 rounded-xl bg-white/5 border-white/10 hover:bg-white/10 ${
                          date ? "text-white" : "text-white/30"
                        }`}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-white/50" />
                        {date ? format(date, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(d) => {
                          setDate(d);
                          setCalendarOpen(false);
                        }}
                        disabled={(d) => d < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Event Type */}
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Event Type *</Label>
                  <Select
                    onValueChange={(value) =>
                      setFormData({ ...formData, eventType: value })
                    }
                    value={formData.eventType}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white h-12 rounded-xl data-[placeholder]:text-white/30">
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {eventTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Venue */}
                <div className="space-y-2">
                  <Label htmlFor="venue" className="text-white/70 text-sm">
                    Venue
                  </Label>
                  <Input
                    id="venue"
                    name="venue"
                    value={formData.venue}
                    onChange={handleChange}
                    placeholder="Venue name (if known)"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-xl focus:border-gold focus:ring-gold"
                  />
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-white/70 text-sm">
                    Tell Us About Your Event
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Share your vision, any special requests, or questions..."
                    rows={4}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl resize-none focus:border-gold focus:ring-gold"
                  />
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gold hover:bg-gold-dark text-white font-semibold h-14 rounded-full text-base"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Send Inquiry
                      <Send className="h-4 w-4" />
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
